import { Component, ElementRef, Inject, Input, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgxGroupService, Org } from '@go-modules/services/group/group.service';
import { User } from '@go-modules/interfaces/user';
import { clientSettings } from 'go-modules/models/common/client.settings';
import { Masquerade, masqueradeToken } from 'go-modules/masquerade/masquerade.service';
import { MatDialog } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { catchError, finalize, map, shareReplay } from 'rxjs/operators';
import { MessageDialogComponent } from '../dialogs/message-dialog/message-dialog.component';

@Component({
	selector: 'org-settings',
	standalone: true,
	imports: [
		CommonModule,
		FormsModule,
		MatTooltipModule,
		TranslateModule
	],
	templateUrl: './org-settings.component.html',
	styleUrls: ['./org-settings.component.scss']
})
export class OrgSettingsComponent implements OnInit {
	@Input() public group: Org;
	@Input() public user: User;

	public isLoading = signal(false);
	public accounts$: Observable<any[]> = of([]);
	public visibleAccounts$: Observable<any[]> = of([]);
	public options = { selectedAccount: false };
	public adminToolsUrl = '';
	public accountFilter$ = new BehaviorSubject('');

	constructor (
		private elementRef: ElementRef,
		private groupService: NgxGroupService,
		private dialog: MatDialog,
		private translate: TranslateService,
		@Inject(masqueradeToken) private masquerade: Masquerade
	) {}

	public ngOnInit () {
		this.adminToolsUrl = `${clientSettings.AdminToolsUrl}#/tool/org-settings?searchType=org&group=${this.group.group_id}`;
		this.isLoading.set(true);
		this.accounts$ = this.groupService.getOrgAccountsWithStats(this.group.group_id).pipe(
			catchError(() => of([])),
			finalize(() => this.isLoading.set(false)),
			shareReplay({ bufferSize: 1, refCount: true })
		);
		this.visibleAccounts$ = combineLatest([
			this.accounts$,
			this.accountFilter$
		]).pipe(
			map(([accounts, filter]) => {
				const sorted = [...accounts].sort((left, right) => {
					const leftName = left.name.toLowerCase();
					const rightName = right.name.toLowerCase();
					return leftName.localeCompare(rightName);
				});

				const normalizedFilter = filter.toLowerCase().trim();
				if (!normalizedFilter) {
					return sorted;
				}

				return sorted.filter((account) => {
					return account.name.toLowerCase().includes(normalizedFilter) ||
						account.user.full_name.toLowerCase().includes(normalizedFilter);
				});
			})
		);
	}

	public clearSearch () {
		this.accountFilter$.next('');
		setTimeout(() => {
			(this.elementRef.nativeElement as HTMLElement)
				.querySelector<HTMLInputElement>('.search-input')
				?.focus();
		});
	}

	public onAccountFilterChange (value: string) {
		this.accountFilter$.next(value);
	}

	public loginAs (user) {
		return this.masquerade.as(user).catch(() => {
			this.dialog.open(MessageDialogComponent, {
				data: {
					title: this.translate.instant('mask-user-error_title'),
					message: this.translate.instant('mask-user-error_message')
				}
			});
		});
	}
}
